import {
  ActionMenu,
  Badge,
  Banner,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FormDialog,
  Skeleton,
  TextField,
  useToast,
  type Column,
} from "@omony/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { api, errorMessageId } from "../../shared/api.js";
import { queryClient } from "../../shared/query.js";
import { StatusBadge } from "../../verwaltung/common.js";
import { StepFrame, useT } from "../flow/common.js";

interface Staff {
  id: string;
  displayName: string;
  status: string;
  locked: boolean;
  mustChangePin: boolean;
}

/** Apotheken-Konto (F4): eigene Mitarbeitende und ihre Kiosk-PINs verwalten */
export function StaffAdmin() {
  const t = useT();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["pharmacy-staff"],
    queryFn: () => api<{ items: Staff[] }>("/pharmacy/staff"),
  });
  const [dialog, setDialog] = useState<{ mode: "create" } | { mode: "pin"; id: string; name: string } | null>(
    null,
  );
  const [remove, setRemove] = useState<Staff | null>(null);
  const action = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      api(`/pharmacy/staff/${id}`, { method: "PATCH", body: { action } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pharmacy-staff"] });
      toast(t("admin.saved"));
    },
    onError: (e) => toast(t(errorMessageId(e)), "danger"),
  });

  const cols: Column<Staff>[] = [
    { key: "name", header: t("admin.staff.name"), cell: (r) => <bdi>{r.displayName}</bdi> },
    {
      key: "status",
      header: t("admin.status.label"),
      cell: (r) => (
        <>
          <StatusBadge status={r.status} temporarilyLocked={r.locked} />
          {r.mustChangePin ? <Badge tone="neutral">{t("admin.staff.pinPending")}</Badge> : null}
        </>
      ),
    },
    {
      key: "a",
      header: <span className="omony-visually-hidden">{t("common.actions")}</span>,
      align: "end",
      cell: (r) => (
        <ActionMenu
          label={r.displayName}
          actions={[
            { id: "pin", label: t("admin.staff.resetPin") },
            r.status === "gesperrt" || r.locked
              ? { id: "entsperren", label: t("admin.action.unlock") }
              : { id: "sperren", label: t("admin.action.lock") },
            { id: "deaktivieren", label: t("admin.staff.remove"), danger: true },
          ]}
          onAction={(a) => {
            if (a === "pin") setDialog({ mode: "pin", id: r.id, name: r.displayName });
            else if (a === "deaktivieren") setRemove(r);
            else action.mutate({ id: r.id, action: a });
          }}
        />
      ),
    },
  ];

  return (
    <StepFrame
      title={t("console.staff.title")}
      lead={t("admin.staff.lead")}
      actions={
        <Button icon={<UserPlus />} onPress={() => setDialog({ mode: "create" })}>
          {t("admin.staff.create")}
        </Button>
      }
    >
      {q.isLoading ? (
        <Skeleton lines={4} />
      ) : q.error ? (
        <Banner tone="danger">{t(errorMessageId(q.error))}</Banner>
      ) : (
        <DataTable
          caption={t("console.staff.title")}
          columns={cols}
          rows={(q.data?.items ?? []).filter((s) => s.status !== "deaktiviert")}
          rowKey={(r) => r.id}
          empty={<EmptyState icon={<Users size={22} />} title={t("admin.staff.empty")} />}
        />
      )}
      {dialog ? <StaffDialog state={dialog} onClose={() => setDialog(null)} /> : null}
      <ConfirmDialog
        isOpen={!!remove}
        onOpenChange={(o) => !o && setRemove(null)}
        title={remove ? t("admin.staff.removeConfirm", { name: remove.displayName }) : ""}
        confirmLabel={t("admin.staff.remove")}
        tone="danger"
        onConfirm={() => remove && action.mutate({ id: remove.id, action: "deaktivieren" })}
      />
    </StepFrame>
  );
}

function StaffDialog({
  state,
  onClose,
}: {
  state: { mode: "create" } | { mode: "pin"; id: string; name: string };
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const m = useMutation({
    mutationFn: () =>
      state.mode === "create"
        ? api("/pharmacy/staff", { method: "POST", body: { displayName: name, pin } })
        : api(`/pharmacy/staff/${state.id}`, { method: "PATCH", body: { pin } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pharmacy-staff"] });
      toast(t("admin.saved"));
      onClose();
    },
  });
  return (
    <FormDialog
      isOpen
      onOpenChange={(o) => !o && onClose()}
      title={
        state.mode === "create" ? t("admin.staff.create") : t("admin.staff.resetPinFor", { name: state.name })
      }
      actions={
        <>
          <Button variant="secondary" onPress={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onPress={() => m.mutate()}
            isDisabled={m.isPending || !/^\d{6}$/.test(pin) || (state.mode === "create" && !name.trim())}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      {m.error ? <Banner tone="danger">{t(errorMessageId(m.error))}</Banner> : null}
      {state.mode === "create" ? (
        <TextField
          label={t("admin.staff.name")}
          value={name}
          onChange={setName}
          description={t("admin.staff.nameHint")}
          isRequired
        />
      ) : null}
      <TextField
        label={t("admin.staff.pin")}
        code
        type="password"
        inputMode="numeric"
        autoComplete="off"
        value={pin}
        onChange={(v) => setPin(v.replace(/\D/g, "").slice(0, 6))}
        description={t("admin.staff.pinHint")}
      />
    </FormDialog>
  );
}
